import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "enable-notifications-cta"
}

import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

import { storage } from '@/helpers/storage'
import { StorageKeys } from '@/helpers/storage.keys'
import { notificationService } from '@/services/notification'
import { platform } from '@/services/platform'
import { openNativeAppSettings } from '@/services/capacitor.appSettingsPlugin'
import { EnableNotificationsCtaTimings } from '@/models/interfaces'
import { Mixpanel } from '@/analytics/mixpanel'
import { backend } from '@/services/backend'
import { Sentry } from '@/services/sentry'
import { isAnyNotificationPreferenceSet } from '@/models/settings'

import AppButton from '@/app/components/button/AppButton.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'EnableNotificationsCTA',
  setup(__props) {

const router = useRouter()

const isVisible = ref(false)
const hasAppNotificationsEnabled = ref(false)

onMounted(async () => {
  isVisible.value = await shouldShowCta()
  if (isVisible.value === true) {
    Mixpanel.trackEnableNotificationsBannerSeen()
  }
})

/**
 * A series of checks to determine if we should render this component.
 *
 * It should only render:
 * - On native apps.
 * - If either device-level or app-level notifications
 *   are not enabled.
 * - And enough time has passed since the user last dismissed
 *   this CTA (see `getNotificationsCtaTiming`).
 */
async function shouldShowCta(): Promise<boolean> {
  const isNativeApp = await platform.isNativeApp()
  if (!isNativeApp) {
    return false
  }

  const deviceNotifications = await notificationService.checkPermission()
  if (!deviceNotifications) {
    // If this is undefined, it means that the current device does not
    // support notifications.
    return false
  }

  hasAppNotificationsEnabled.value = await checkAppNotificationsEnabled()
  if (deviceNotifications === 'granted' && hasAppNotificationsEnabled.value) {
    // This is the most important check: the checks above were just
    // making sure that the device is eligible for this CTA to ever
    // be displayed. Here we check if the device-level or app-level
    // notifications are enabled.
    //
    // If device-level notifications are not enabled, we'll take the user
    // to the device's notifications settings for our app. If app-level
    // notifications are not enabled, we'll take them to the in-app
    // settings page.
    return false
  }

  // Even if notifications are not enabled, we might still
  // not render this CTA if they previously dismissed it.
  const ctaTiming = getNotificationsCtaTiming()
  if (!ctaTiming.nextShowDate) {
    return false
  }

  // Show the CTA if the `nextShowDate` is due.
  return ctaTiming.nextShowDate <= Date.now()
}

/**
 * Check if the user has at least one app-level notification enabled.
 */
async function checkAppNotificationsEnabled(): Promise<boolean> {
  try {
    const notificationsSettings = await backend.getNotificationSettings()
    return isAnyNotificationPreferenceSet(notificationsSettings)
  } catch (error) {
    Sentry.captureException(error)
    return false
  }
}

/**
 * Hide the CTA, but show it again after a few days.
 *
 * After hiding the CTA, we set `notificationsCtaTiming`
 * in local storage with `nextShowDate` as the date when we should
 * show this CTA again.
 */
function hide(): void {
  isVisible.value = false
  const newTimings = setNotificationsCtaTiming()
  Mixpanel.trackEnableNotificationsBannerClose(newTimings.timesDismissed)
}

/**
 * Request push-notifications permission, or take the user to where
 * they can manually them.
 *
 * If the user hasn't granted or denied push-notifications permission
 * on the device, this will show the device's notification permission
 * popup.
 *
 * If the user has denied push-notifications, we can't show the popup
 * again, so we take them to the device's notification settings page,
 * where they can manually enable notifications.
 *
 * If all app-level notifications are disabled, we take them to the
 * app's settings page, where they can enable them.
 */
async function enableNotifications(): Promise<void> {
  Mixpanel.trackEnableNotificationsBannerClick()

  // Hide the CTA.
  isVisible.value = false
  // Reset the notifications hide timing.
  storage.removeItem(StorageKeys.notificationsCtaTiming)

  const deviceNotificationsPermission =
    await notificationService.checkPermission()

  // Request notifications permission if we haven't done that yet
  // (this should be an edge case).
  if (!['granted', 'denied'].includes(deviceNotificationsPermission!)) {
    await notificationService.init(true)
    return
  }

  if (!hasAppNotificationsEnabled.value) {
    router.push({ name: 'settings' })
  }

  if (deviceNotificationsPermission !== 'granted') {
    await openNativeAppSettings()
  }
}

/**
 * Get the notifications CTA hide timings.
 *
 * This object has number of times the CTA has been dismissed
 * (`timesDismissed`), and the date when to start showing it again.
 * in ms (`nextShowDate`).
 */
function getNotificationsCtaTiming(): EnableNotificationsCtaTimings {
  const localStorageData = storage.getItem(StorageKeys.notificationsCtaTiming)

  return localStorageData
    ? JSON.parse(localStorageData)
    : {
        timesDismissed: 0,
        nextShowDate: Date.now(),
      }
}

/**
 * Set the data for showing the CTA again after a few days.
 *
 * The timings are:
 * - 1st dismissal: 7 days
 * - 2nd dismissal: 30 days
 * - 3rd dismissal: 90 days
 * - 4th dismissal: 180 days
 * - 5th dimissal: 360 days
 * - After that don't show the CTA again.
 */
function setNotificationsCtaTiming(): EnableNotificationsCtaTimings {
  const ctaTiming = getNotificationsCtaTiming()
  ctaTiming.timesDismissed += 1

  // This is the number of days that we should
  // hide the CTA, for each number of dismissals.
  const timings = [0, 7, 30, 90, 180, 360]

  const daysToAdd = timings[ctaTiming.timesDismissed] || 0
  const daysToAddInMs = daysToAdd * 24 * 60 * 60 * 1000

  if (!daysToAdd) {
    // If there are no days to add, it means
    // we shouldn't ever show this CTA again.
    ctaTiming.nextShowDate = null
  } else {
    ctaTiming.nextShowDate = Date.now() + daysToAddInMs
  }

  storage.setItem(StorageKeys.notificationsCtaTiming, JSON.stringify(ctaTiming))

  return ctaTiming
}

return (_ctx: any,_cache: any) => {
  return (isVisible.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _cache[2] || (_cache[2] = _createElementVNode("h3", { class: "enable-notifications-cta__title" }, "Stay on track", -1)),
        _cache[3] || (_cache[3] = _createElementVNode("p", { class: "enable-notifications-cta__text" }, " Receive reminders that fit your schedule and help you keep learning consistently ", -1)),
        _createVNode(AppButton, {
          class: "enable-notifications-cta__button",
          onClick: enableNotifications
        }, {
          default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
            _createTextVNode("Turn on notifications", -1)
          ]))]),
          _: 1
        }),
        _createElementVNode("button", {
          class: "enable-notifications-cta__close",
          onClick: hide
        }, [...(_cache[1] || (_cache[1] = [
          _createElementVNode("span", { class: "iconfont iclose-thin" }, null, -1)
        ]))])
      ]))
    : _createCommentVNode("", true)
}
}

})